import React, { useRef, useState, useEffect, useCallback } from 'react'

const SCROLLBAR_WIDTH = 14
const SCROLL_BUTTON_THRESHOLD = 100
/** 距底部小于此值视为「贴底」，内容增高 / store 触发的滚底才生效（与 handleHeightChange 一致） */
const PINNED_TO_BOTTOM_THRESHOLD = 120

export type ScrollToBottomOptions = {
  /**
   * true：始终滚到底（如用户点击「滚到底部」）。
   * false/不传：仅当当前已「贴底」（距底小于 PINNED_TO_BOTTOM_THRESHOLD）时才滚，避免打断看历史。
   */
  force?: boolean
}

/**
 * 主列表容器自动滚动：DOM 增高时仅在贴底时跟随；scrollToBottom 默认同样仅在贴底时生效。
 * 阈值 PINNED_TO_BOTTOM_THRESHOLD 与「快贴到底部」语义一致。
 */
export const useAutoScroll = (containerRef: React.RefObject<HTMLElement>) => {
  const [showScrollButton, setShowScrollButton] = useState(false)
  const [hasScrollbar, setHasScrollbar] = useState(false)
  const prevScrollHeightRef = useRef<number>(0)

  /**
   * 滚到底：默认仅当列表已快贴底（距底 < PINNED_TO_BOTTOM_THRESHOLD）才执行，否则直接 return。
   * `force: true` 时忽略贴底判断（如 store 传强制、用户点回到底部按钮）。
   */
  const scrollToBottom = useCallback((options?: ScrollToBottomOptions) => {
    const container = containerRef.current
    if (!container) return

    const force = Boolean(options?.force)
    if (!force) {
      const { scrollTop, scrollHeight, clientHeight } = container
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight
      if (distanceFromBottom >= PINNED_TO_BOTTOM_THRESHOLD) {
        return
      }
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (container) {
          const { scrollHeight, clientHeight } = container
          container.scrollTop = scrollHeight - clientHeight
          prevScrollHeightRef.current = scrollHeight
        }
      })
    })
  }, [])

  // 检查是否有滚动条
  const checkScrollbar = useCallback(() => {
    const container = containerRef.current
    if (!container) return
    setHasScrollbar(container.scrollHeight > container.clientHeight)
  }, [])

  // 处理滚动事件，控制滚动按钮显示
  const handleScroll = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const { scrollTop, scrollHeight, clientHeight } = container
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight
    setShowScrollButton(distanceFromBottom > SCROLL_BUTTON_THRESHOLD && scrollHeight > clientHeight)
  }, [])

  // 处理高度变化：仅在上次布局下用户已贴底、或强制滚动时，才在增高后跟随到底部
  const handleHeightChange = useCallback((forceScroll = false) => {
    const container = containerRef.current
    if (!container) return

    const { scrollTop, clientHeight } = container
    const currentScrollHeight = container.scrollHeight
    const prevScrollHeight = prevScrollHeightRef.current

    const distanceFromBottomBefore =
      prevScrollHeight > 0 ? prevScrollHeight - scrollTop - clientHeight : 0
    const wasPinnedToBottom =
      prevScrollHeight <= 0 || distanceFromBottomBefore < PINNED_TO_BOTTOM_THRESHOLD

    const shouldStickToBottom =
      forceScroll || (currentScrollHeight > prevScrollHeight && wasPinnedToBottom)

    if (shouldStickToBottom) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (container) {
            const { scrollHeight, clientHeight: ch } = container
            container.scrollTop = scrollHeight - ch
            prevScrollHeightRef.current = scrollHeight
          }
        })
      })
    } else {
      prevScrollHeightRef.current = currentScrollHeight
    }
  }, [])

  // 监听内容变化（DOM 变化）
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let debounceTimer: number | null = null

    const observer = new MutationObserver(() => {
      if (debounceTimer) {
        cancelAnimationFrame(debounceTimer)
      }
      debounceTimer = requestAnimationFrame(() => {
        handleHeightChange(false)
        debounceTimer = null
      })
    })

    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    })

    return () => {
      if (debounceTimer) {
        cancelAnimationFrame(debounceTimer)
      }
      observer.disconnect()
    }
  }, [handleHeightChange])

  // 初始化监听器
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener('scroll', handleScroll)
    checkScrollbar()
    prevScrollHeightRef.current = container.scrollHeight

    const resizeObserver = new ResizeObserver(() => {
      checkScrollbar()
      handleHeightChange(false)
    })

    resizeObserver.observe(container)

    const firstChild = container.firstElementChild as HTMLElement
    if (firstChild) {
      resizeObserver.observe(firstChild)
    }

    return () => {
      container.removeEventListener('scroll', handleScroll)
      resizeObserver.disconnect()
    }
  }, [])

  return {
    showScrollButton,
    hasScrollbar,
    scrollToBottom,
    scrollbarWidth: SCROLLBAR_WIDTH
  }
}
